import { PropsWithChildren, useEffect, useState, useRef } from 'react'
import { executeAfterPricingContextEvent, EVENTS } from '@vp/pricing-context-module/helpers'
import { PricingContext } from '~/app/features/FragmentRoot/contexts/pricing/context'
import { getPricingContextFromBrowser } from '~/app/features/FragmentRoot/contexts/pricing/utils'

export const PricingContextProviderWrapper = ({ children }: PropsWithChildren) => {
  const [pricingContextModule, setPricingContextModule] = useState<PricingContextModule.PcxtV3 | null>(null)
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true

    const initializePricingContextModule = () => {
      const pcxt = getPricingContextFromBrowser()

      if (!pcxt) {
        return false
      }

      const isPcxtReady = pcxt.isInitialized?.() ?? false
      const isMainComponentStillExists = mountedRef.current
      const isNotInitializedYet = !pricingContextModule

      if (isPcxtReady && isMainComponentStillExists && isNotInitializedYet) {
        const setPricingContext = () => setPricingContextModule({ ...pcxt })

        setPricingContext()
        executeAfterPricingContextEvent(EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT, setPricingContext, true)
        executeAfterPricingContextEvent(EVENTS.DATE_TIME_CHANGE_EVENT, setPricingContext, true)
        executeAfterPricingContextEvent(EVENTS.CONTEXT_CHANGE_EVENT, setPricingContext, true)
        executeAfterPricingContextEvent(EVENTS.COUPON_CHANGE_EVENT, setPricingContext, true)

        return true
      }

      return false
    }

    const isPricingContextSet = initializePricingContextModule()
    if (!isPricingContextSet) {
      const handleInitializedEvent = () => initializePricingContextModule()

      document.addEventListener(EVENTS.INITIALIZED_EVENT, handleInitializedEvent)

      // cleanup
      return () => {
        mountedRef.current = false
        document.removeEventListener(EVENTS.INITIALIZED_EVENT, handleInitializedEvent)
      }
    }

    // cleanup without event listener
    return () => {
      mountedRef.current = false
    }
  }, [])

  return <PricingContext.Provider value={pricingContextModule}>{children}</PricingContext.Provider>
}
